import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  //   #handleVariantUpdate = (event) => {
  //   const source = event.detail.data.html;

  //   if (!source) return;
  //   const newMediaGallery = source.querySelector('media-gallery');

  //   if (!newMediaGallery) return;

  //   // PDP: No change needed here; perform a standard replace
  //   this.replaceWith(newMediaGallery);
  // };
  #handleVariantUpdate = (event) => {
    const variantResource = event.detail.resource;

    if (!variantResource || !this.slideshow) return;

    // Get the featured media ID from the variant
    const featuredMediaId = variantResource.featured_media?.id;

    if (featuredMediaId) {
      // Select the variant's featured media in the slideshow
      this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
    }
  };

  /**
   * Keeps the selected color in sync when a customer changes the main image
   * by swiping or using the gallery controls.
   *
   * @param {SlideshowSelectEvent} event - The slideshow selection event.
   */
  #handleSlideshowSelect = (event) => {
    if (!event.detail.userInitiated) return;

    const slide = event.detail.slide;
    const colorValue = slide?.dataset.variantColorValue;
    const optionPosition = slide?.dataset.variantColorOptionPosition;

    if (!colorValue || !optionPosition) return;

    const target = this.closest('.shopify-section, dialog');
    const optionInputs = target?.querySelectorAll(
      `variant-picker input[type="radio"][data-input-id^="${optionPosition}-"]`
    );
    const matchingInput = Array.from(optionInputs ?? []).find((input) => input.value === colorValue);

    // `aria-disabled` is informational in this theme: the variant picker can
    // still pivot to the first available size for the selected color.
    if (!matchingInput || matchingInput.checked) return;

    matchingInput.click();
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
